import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Dialog, DialogContent, Grid, IconButton, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isAfter } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import { BlockContainer, Container, Field, Label } from "./styles";
import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import CustomConfirmationChangesDialog from "../../../../containers/CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import FindOrCreateUser from "../../../../containers/FindOrCreateUser/FindOrCreateUser";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import { PropsFilter } from "../../../../models/api/props-filter";
import { CompanyAccountRef } from "../../../../models/company-accounts/company-accounts";
import { CompanyLegalEntityRef } from "../../../../models/company-accounts/company-legal-entities";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import { EmployeeRef } from "../../../../models/employees";
import { TeamMemberDTO } from "../../../../models/team-member/team-member-dto";
import { TeamMemberSTO } from "../../../../models/team-member/team-member-sto";
import { UserDTO, UserRef, UserSTO } from "../../../../models/user";
import { UserStatusRef } from "../../../../models/user-status";
import AddEditButton from "../../../../reusable-components/AddEditButton/AddEditButton";
import { CompanyLegalEntitiesApiService } from "../../../../services/company-legal-entities";
import { TeamMembersApiService } from "../../../../services/team-members/team-members-api.service";
import { UsersApiService } from "../../../../services/users";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import { USER_STATUS } from "../../../../utils/constants/constants";
import { EMAIL_PATTERN, isValidString } from "../../../../utils/validation/isValidString";

interface Props {
  item?: TeamMemberDTO;
  handleNotifyParent: () => void;
  children?: React.ReactNode;
}

interface ApproverData {
  id: string;
  existingUser?: UserRef;
  newUser?: UserSTO;
}

interface State {
  item: TeamMemberDTO;
  loading: boolean;
  error: string;
  open: boolean;
  CCDOpen: boolean;
  approverData: ApproverData[];
  isValidItem: boolean;
  authorizedLELoading: boolean;
  savingCCDOpen: boolean;
}

const AddEditTeamMember = ({ item, handleNotifyParent, children }: Props) => {
  const tRP = "components.add_edit_team_member";
  const { t } = useTranslation();

  const { currentCompanyAccount } = useAppSelector((state) => state.currentUserReducer);

  const DEFAULT_EMPLOYEE: EmployeeRef = useMemo(
    () => ({
      id: "",
      legalEntity: null,
      companyAccount: null,
      isDeleted: false,
      firstName: "",
      lastName: "",
      name: "",
      email: "",
      employeeCode: "",
      taxId: "",
      birthDate: null,
      department: "",
      groups: [],
      tnAGroup: null,
      internalId: 0,
    }),
    []
  );

  const DEFAULT_ITEM: TeamMemberSTO = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      companyAccount: currentCompanyAccount as CompanyAccountRef,
      legalEntity: null,
      employee: DEFAULT_EMPLOYEE,
      phoneNumber: "",
      startDate: CustomDate.FromDate(new Date()),
      endDate: null,
      approvers: [],
    }),
    [currentCompanyAccount, DEFAULT_EMPLOYEE]
  );

  const [state, setState] = useState<State>({
    item: item ?? (DEFAULT_ITEM as TeamMemberDTO),
    loading: false,
    error: "",
    open: false,
    CCDOpen: false,
    approverData: item?.approvers?.length
      ? item.approvers.map((approver) => ({ id: uuid(), existingUser: approver }))
      : [{ id: uuid() }],
    isValidItem: !!item,
    authorizedLELoading: false,
    savingCCDOpen: false,
  });

  // #region handle open dialog
  const handleOpenDialog = useCallback(() => {
    setState((previousState) => ({
      ...previousState,
      open: true,
      item: item ?? (DEFAULT_ITEM as TeamMemberDTO),
      approverData: item?.approvers?.length
        ? item.approvers.map((approver) => ({ id: uuid(), existingUser: approver }))
        : [{ id: uuid() }],
      isValidItem: !!item,
    }));
  }, [item, DEFAULT_ITEM]);
  // #endregion

  // #region getLegalEntity
  const getLegalEntity = useCallback(async () => {
    setState((previousState) => ({
      ...previousState,
      authorizedLELoading: true,
    }));
    try {
      const response = await new CompanyLegalEntitiesApiService().loadItems({
        contextAuthorizations: [AUTHORIZATIONS.LegalEntityAccess.SimplifiedTeamManagement],
      } as PropsFilter);
      if (response.error) {
        setState((previousState) => ({ ...previousState, error: response.error }));
      } else {
        const legalEntity = response.items[0];
        setState((previousState) => ({
          ...previousState,
          item: {
            ...state.item,
            legalEntity: legalEntity as CompanyLegalEntityRef,
            employee: {
              ...state.item.employee,
              legalEntity: legalEntity as CompanyLegalEntityRef,
            } as EmployeeRef,
          },
        }));
      }
    } catch (error) {
      setState((previousState) => ({ ...previousState, error: error as string }));
    } finally {
      setState((previousState) => ({
        ...previousState,
        authorizedLELoading: false,
      }));
    }
  }, [state.item]);
  // #endregion

  useEffect(() => {
    if (!item && state.open) getLegalEntity();
  }, [item, state.open]);

  // #region change employee data
  const handleChangeEmployeData = useCallback((field: keyof EmployeeRef, value: EmployeeRef[keyof EmployeeRef]) => {
    setState((previousState) => ({
      ...previousState,
      item: {
        ...previousState.item,
        employee: {
          ...(previousState.item.employee as EmployeeRef),
          [field]: value,
        },
      },
    }));
  }, []);
  // #ednregion

  // #region handle change team member data
  const handleChangeTeamMemberData = useCallback(
    (field: keyof TeamMemberSTO, value: TeamMemberSTO[keyof TeamMemberSTO]) => {
      setState((previousState) => ({
        ...previousState,
        item: {
          ...previousState.item,
          [field]: value,
        },
      }));
    },
    []
  );
  // #endregion

  // #region discard
  const handleDiscard = useCallback(() => {
    setState({
      item: item ?? (DEFAULT_ITEM as TeamMemberDTO),
      loading: false,
      error: "",
      open: false,
      CCDOpen: false,
      approverData: item?.approvers?.length
        ? item.approvers.map((approver) => ({ id: uuid(), existingUser: approver }))
        : [{ id: uuid() }],
      isValidItem: !!item,
      authorizedLELoading: false,
      savingCCDOpen: false,
    });
  }, [DEFAULT_ITEM, item]);

  // #region save approver users
  const saveApproverUsers = useCallback(async (approverData: ApproverData[]): Promise<UserRef[]> => {
    const savedApprovers: UserRef[] = [];
    const newUsersToSave = approverData.filter((data) => data.newUser).map((data) => data.newUser!);

    for (const user of newUsersToSave) {
      const newUser = { ...user, id: uuid() };
      try {
        const response = await new UsersApiService().saveItems([
          { ...newUser, status: USER_STATUS.active as UserStatusRef },
        ]);
        if (response.error) {
          setState((previousState) => ({ ...previousState, error: response.error }));
        } else {
          savedApprovers.push(newUser as UserRef);
        }
      } catch (error) {
        setState((previousState) => ({ ...previousState, error: error as string }));
      }
    }
    return savedApprovers;
  }, []);
  // #endregion

  // #region handle add approver
  const handleAddApprover = useCallback(() => {
    setState((previousState) => ({
      ...previousState,
      approverData: [...previousState.approverData, { id: uuid() }],
    }));
  }, []);
  // #endregion

  // #region handle remove approver
  const handleRemoveApprover = useCallback((index: number) => {
    setState((previousState) => {
      const approverData = [...previousState.approverData];
      approverData.splice(index, 1);

      if (approverData.length === 0) {
        approverData.push({ id: uuid() });
      }

      return {
        ...previousState,
        approverData,
      };
    });
  }, []);
  // #endregion

  // #region handle approver change
  const handleApproverChange = useCallback((index: number, user: UserDTO | null) => {
    setState((previousState) => {
      const approverData = [...previousState.approverData];
      const currentId = approverData[index].id;
      if (user) {
        approverData[index] = { id: currentId, existingUser: user as UserRef };
      } else {
        approverData[index] = { id: currentId };
      }
      return {
        ...previousState,
        approverData,
      };
    });
  }, []);
  // #endregion

  // #region handle new approver
  const handleNewApprover = useCallback((index: number, user: UserSTO | null) => {
    setState((previousState) => {
      const approverData = [...previousState.approverData];
      const currentId = approverData[index].id;
      if (user) {
        approverData[index] = { id: currentId, newUser: user };
      } else {
        approverData[index] = { id: currentId };
      }
      return {
        ...previousState,
        approverData,
      };
    });
  }, []);
  // #endregion

  // #region save team member
  const handleSaveTeamMember = useCallback(
    async (newlySavedApprovers: UserRef[] = []) => {
      setState((previousState) => ({ ...previousState, loading: true }));
      try {
        const existingApprovers = state.approverData
          .filter((data) => data.existingUser)
          .map((data) => data.existingUser!);

        const allApprovers = [...existingApprovers, ...newlySavedApprovers];

        const teamMember: TeamMemberSTO = {
          ...state.item,
          approvers: allApprovers,
          ...(!item && { id: uuid(), employee: { ...state.item.employee, id: uuid() } as EmployeeRef }),
        };
        const response = await new TeamMembersApiService().saveItems([{ ...teamMember }]);
        if (response.error) {
          setState((previousState) => ({ ...previousState, error: response.error, savingCCDOpen: false }));
        } else {
          handleNotifyParent && handleNotifyParent();
          setState((previousState) => ({
            ...previousState,
            error: "",
            open: false,
            ...(!item && {
              item: DEFAULT_ITEM as TeamMemberDTO,
            }),
            approverData: [{ id: uuid() }],
          }));
        }
      } catch (error) {
        setState((previousState) => ({ ...previousState, error: error as string }));
      } finally {
        setState((previousState) => ({ ...previousState, loading: false }));
      }
    },
    [handleNotifyParent, state.item, state.approverData, item, DEFAULT_ITEM]
  );
  // #endregion

  // #region save approvers then member
  const saveApproversThenTeamMember = useCallback(async () => {
    const hasNewUsers = state.approverData.some((data) => data.newUser);
    if (hasNewUsers) {
      const savedApprovers = await saveApproverUsers(state.approverData);
      if (savedApprovers.length > 0) {
        handleSaveTeamMember(savedApprovers);
      }
    }
  }, [state.approverData, handleSaveTeamMember, saveApproverUsers]);
  // #endregion

  // #region pre-save team member
  const prehandleSaveTeamMember = useCallback(() => {
    // show toast if duplicates found
    if (state.error) {
      toast.error(state.error);
      return;
    }

    const hasNewUsers = state.approverData.some((data) => data.newUser);
    const hasEmployeeEmailChanged = !item || item.employee?.email !== state.item.employee?.email;

    const hasApproversChanged =
      !item ||
      item.approvers?.length !== state.approverData.filter((d) => d.existingUser || d.newUser).length ||
      state.approverData.some((data) => {
        const email = data.newUser?.email || data.existingUser?.email;
        return email && !item.approvers?.some((approver) => approver.email === email);
      });

    if (!item || hasNewUsers || hasEmployeeEmailChanged || hasApproversChanged) {
      setState((previousState) => ({ ...previousState, savingCCDOpen: true }));
    } else {
      handleSaveTeamMember();
    }
  }, [state.approverData, state.error, state.item.employee?.email, item, handleSaveTeamMember]);
  // #endregion

  // #region check duplicate emails
  const checkDuplicateEmails = useCallback(() => {
    const emailMap = new Map<string, number[]>();

    state.approverData.forEach((data, index) => {
      const email = data.existingUser?.email || data.newUser?.email;
      if (email && email.trim()) {
        const normalizedEmail = email.toLowerCase().trim();
        if (!emailMap.has(normalizedEmail)) {
          emailMap.set(normalizedEmail, []);
        }
        emailMap.get(normalizedEmail)!.push(index);
      }
    });

    let hasDuplicates = false;
    emailMap.forEach((indices) => {
      if (indices.length > 1) {
        hasDuplicates = true;
      }
    });

    if (hasDuplicates) {
      setState((previousState) => ({ ...previousState, error: t(`${tRP}.duplicate_email_error`) }));
      return false;
    } else {
      setState((previousState) => ({ ...previousState, error: "" }));
      return true;
    }
  }, [state.approverData, t, tRP]);
  // #endregion

  // #region is valid item
  const isValidItem = useCallback(() => {
    const hasApprovers = state.approverData.some((data) => data.existingUser || data.newUser);
    const noDuplicates = checkDuplicateEmails();
    const isValid =
      !!state.item.startDate &&
      hasApprovers &&
      noDuplicates &&
      !!state.item.employee?.email &&
      isValidString(EMAIL_PATTERN, state.item.employee.email) &&
      !!state.item.employee.firstName &&
      !!state.item.employee.lastName &&
      ((!!state.item.startDate &&
        !!state.item.endDate &&
        isAfter(CustomDate.ToDate(state.item.endDate)!, CustomDate.ToDate(state.item.startDate)!)) ||
        !state.item.endDate);

    setState((previousState) => ({ ...previousState, isValidItem: isValid }));
  }, [state.item, state.approverData, checkDuplicateEmails]);
  // #endregion

  useEffect(() => {
    isValidItem();
  }, [state.item, state.approverData]);
  return (
    <>
      {children ? (
        <Grid onClick={handleOpenDialog}>{children}</Grid>
      ) : (
        <AddEditButton
          title={`${item ? t(`common_words.edit`) : t(`common_words.add`)}`}
          onClick={handleOpenDialog}
          effect={item ? "edit" : "add"}
          {...(item && { buttonText: `${t(`common_words.edit`)}` })}
        />
      )}
      <Dialog
        open={state.open}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              maxWidth: "fit-content !important",
              // maxHeight: "90vh",
            },
          },
        }}
      >
        <DialogTitle
          title={`${item ? t(`${tRP}.edit_title`) : t(`${tRP}.add_title`)}`}
          handleClose={() => setState((previousState) => ({ ...previousState, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            {state.item.legalEntity ? (
              <Container>
                <BlockContainer>
                  <Field>
                    <Label>{`${t(`${tRP}.first_name`)}`} *</Label>
                    <TextField
                      value={state.item.employee?.firstName}
                      onChange={(e) => handleChangeEmployeData("firstName", e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label>{`${t(`${tRP}.last_name`)}`} *</Label>
                    <TextField
                      value={state.item.employee?.lastName}
                      onChange={(e) => handleChangeEmployeData("lastName", e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label>{`${t(`common_words.code`)}`} *</Label>
                    <TextField
                      value={state.item.employee?.employeeCode}
                      onChange={(e) => handleChangeEmployeData("employeeCode", e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label>{`${t(`${tRP}.email_address`)}`} *</Label>
                    <TextField
                      value={state.item.employee?.email}
                      onChange={(e) => handleChangeEmployeData("email", e.target.value)}
                      error={
                        state.item.employee?.email ? !isValidString(EMAIL_PATTERN, state.item.employee?.email) : false
                      }
                      helperText={
                        state.item.employee?.email && !isValidString(EMAIL_PATTERN, state.item.employee?.email)
                          ? `${t(`${tRP}.please_enter_valid_email`)}`
                          : ""
                      }
                    />
                  </Field>
                  <Field>
                    <Label>{`${t(`${tRP}.phone_number`)}`}</Label>
                    <TextField
                      value={state.item.phoneNumber}
                      onChange={(e) => handleChangeTeamMemberData("phoneNumber", e.target.value)}
                    />
                  </Field>
                </BlockContainer>
                <BlockContainer>
                  <Field>
                    <Label>{`${t(`${tRP}.start_date`)}`} *</Label>
                    <DesktopDatePicker
                      onChange={(value) => {
                        handleChangeTeamMemberData("startDate", value ? CustomDate.FromDate(new Date(value)) : null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          // label={t(`${tRP}.start_date`)}
                        />
                      )}
                      inputFormat="dd.MM.yyyy"
                      value={state.item.startDate ? CustomDate.ToDate(state.item.startDate) ?? null : null}
                      mask="__.__.____"
                    />
                  </Field>
                  <Field>
                    <Label>{`${t(`${tRP}.end_date`)}`}</Label>
                    <DesktopDatePicker
                      onChange={(value) => {
                        handleChangeTeamMemberData("endDate", value ? CustomDate.FromDate(new Date(value)) : null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          helperText={params.error ? `${t(`${tRP}.end_date_error`)}` : ""}
                          error={
                            !!state.item.startDate &&
                            !!state.item.endDate &&
                            isAfter(CustomDate.ToDate(state.item.startDate)!, CustomDate.ToDate(state.item.endDate)!)
                          }
                        />
                      )}
                      inputFormat="dd.MM.yyyy"
                      value={state.item.endDate ? CustomDate.ToDate(state.item.endDate) ?? null : null}
                      mask="__.__.____"
                      minDate={state.item.startDate ? CustomDate.ToDate(state.item.startDate) ?? null : null}
                    />
                  </Field>
                  <Field>
                    <Label>{`${t(`${tRP}.approvers`)}`} *</Label>
                    <Grid style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {state.approverData.map((data, index) => {
                        const user = data.existingUser || null;
                        return (
                          <Grid
                            key={data.id}
                            style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
                          >
                            <Grid style={{ flex: 1 }}>
                              <FindOrCreateUser
                                displayLabel={false}
                                user={user}
                                handleUser={(user: UserDTO | null) => handleApproverChange(index, user)}
                                handleNewUser={(user: UserSTO | null) => handleNewApprover(index, user)}
                                customContainerStyle={{
                                  flexDirection: "column",
                                }}
                                customNewUserBlockStyle={{
                                  flexDirection: "column",
                                }}
                                displayWarning={false}
                                customTRP={`${tRP}.find_or_create_user`}
                                displayUserAvailabilityHints={false}
                              />
                            </Grid>
                            {state.approverData.length > 1 && (
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleRemoveApprover(index)}
                                style={{ marginTop: "8px" }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </IconButton>
                            )}
                          </Grid>
                        );
                      })}
                      <Grid style={{ marginTop: "8px" }}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={handleAddApprover}
                          title={t(`${tRP}.add_approver`)}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Field>
                </BlockContainer>
              </Container>
            ) : state.authorizedLELoading ? (
              <></>
            ) : (
              <Alert severity="warning">{`${t(`${tRP}.missing_entity_warning`)}`}</Alert>
            )}
          </LoadingOverlay>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((previousState) => ({ ...previousState, CCDOpen: true }))}
          handleSave={prehandleSaveTeamMember}
          disabledSaveButton={!state.isValidItem}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((previousState) => ({ ...previousState, CCDOpen: false }))}
        handleDiscard={handleDiscard}
      />
      <CustomConfirmationChangesDialog
        open={state.savingCCDOpen}
        loading={state.loading}
        title={`${t(`${tRP}.confirmation_dialog.title`)}`}
        handleCancel={() => setState((previousState) => ({ ...previousState, savingCCDOpen: false }))}
        actionsAndHandlers={[
          {
            action: `${t(`${tRP}.confirmation_dialog.yes_save`)}`,
            handler: () => {
              setState((previousState) => ({ ...previousState, savingCCDOpen: false }));
              const hasNewUsers = state.approverData.some((data) => data.newUser);
              if (hasNewUsers) {
                saveApproversThenTeamMember();
              } else {
                handleSaveTeamMember();
              }
            },
            buttonVariant: "contained",
            customButtonStyle: {
              textTransform: "none",
            },
          },
        ]}
      >
        <Grid style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <Typography>{`${t(`${tRP}.confirmation_dialog.you_have_entered`)}`}</Typography>
          {(!item || item.employee?.email !== state.item?.employee?.email) && (
            <>
              <Typography>
                &nbsp; &nbsp;
                <Trans
                  i18nKey={`${tRP}.confirmation_dialog.employee_email`}
                  values={{ employeeEmail: state.item.employee?.email }}
                  components={{ b: <b></b> }}
                />
              </Typography>
            </>
          )}
          {state.approverData.some((data) => data.newUser || data.existingUser) && (
            <>
              {state.approverData.map((data, index) => {
                const email = data.newUser?.email || data.existingUser?.email;
                if (email) {
                  return (
                    <Typography key={index}>
                      &nbsp; &nbsp;
                      <Trans
                        i18nKey={`${tRP}.confirmation_dialog.approver_email`}
                        values={{ approverEmail: email }}
                        components={{ b: <b></b> }}
                      />
                    </Typography>
                  );
                }
                return null;
              })}
            </>
          )}
          <br />
          <Typography>{`${t(`${tRP}.confirmation_dialog.confirm_question`)}`}</Typography>
        </Grid>
      </CustomConfirmationChangesDialog>
    </>
  );
};

export default AddEditTeamMember;
